import queryString from "query-string";
import { AnalyseQueryParams } from "./types";
import { parseAnalyseQueryParams } from "./utils";

export const INCIDENTS_QUERY_KEYS = {
  modelId: "modelId",
  secondaryModelId: "secondaryModelId",
  projectId: "projectId",
  batchId: "batchId",
  mediaId: "mediaId",
} as const;

export type IncidentsUrlSelection = {
  modelId: string | null;
  secondaryModelId: string | null;
  projectId: string | null;
  batchId: number | null;
  mediaId: string | null;
};

const readParam = (params: AnalyseQueryParams, names: string[]): string | null => {
  for (const name of names) {
    const value = params[name];
    if (value === undefined || value === "") continue;
    return String(value);
  }
  return null;
};

const readBatchId = (params: AnalyseQueryParams): number | null => {
  const raw = readParam(params, [INCIDENTS_QUERY_KEYS.batchId, "batch", "lotId"]);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const parseIncidentsQueryParams = (
  params: AnalyseQueryParams
): IncidentsUrlSelection => ({
  modelId: readParam(params, [INCIDENTS_QUERY_KEYS.modelId, "model"]),
  secondaryModelId: readParam(params, [
    INCIDENTS_QUERY_KEYS.secondaryModelId,
    "secondaryModel",
    "model2",
  ]),
  projectId: readParam(params, [INCIDENTS_QUERY_KEYS.projectId, "project"]),
  batchId: readBatchId(params),
  mediaId: readParam(params, [
    INCIDENTS_QUERY_KEYS.mediaId,
    "media",
    "videoId",
    "video",
  ]),
});

export const buildIncidentsQueryParams = (
  baseParams: AnalyseQueryParams,
  selection: IncidentsUrlSelection
): AnalyseQueryParams => {
  const next: AnalyseQueryParams = { ...baseParams };

  const setOrRemove = (key: string, value: string | null) => {
    if (value == null || value === "") {
      delete next[key];
      return;
    }
    next[key] = value;
  };

  setOrRemove(INCIDENTS_QUERY_KEYS.modelId, selection.modelId);
  setOrRemove(
    INCIDENTS_QUERY_KEYS.secondaryModelId,
    selection.secondaryModelId
  );
  setOrRemove(INCIDENTS_QUERY_KEYS.projectId, selection.projectId);
  setOrRemove(
    INCIDENTS_QUERY_KEYS.batchId,
    selection.batchId == null ? null : String(selection.batchId)
  );
  setOrRemove(INCIDENTS_QUERY_KEYS.mediaId, selection.mediaId);

  return next;
};

export const buildIncidentsSearch = (
  currentSearch: string,
  selection: IncidentsUrlSelection
): string => {
  const base = parseAnalyseQueryParams(currentSearch);
  const merged = buildIncidentsQueryParams(base, selection);
  const serialized = queryString.stringify(merged);
  return serialized ? `?${serialized}` : "";
};

export const selectionFromStore = (store: {
  selectedModelId: string | null;
  selectedSecondaryModelId: string | null;
  selectedProjectId: string | null;
  selectedBatchId: number | null;
  selectedVideoId: string | null;
}): IncidentsUrlSelection => ({
  modelId: store.selectedModelId,
  secondaryModelId: store.selectedSecondaryModelId,
  projectId: store.selectedProjectId,
  batchId: store.selectedBatchId,
  mediaId: store.selectedVideoId,
});
